import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { projectsAPI } from '../services/api';
import { Project } from '../types';
import { useAuth } from './AuthContext';

interface ProjectContextType {
  currentProject: Project | null;
  projects: Project[];
  setCurrentProject: (project: Project | null) => void;
  selectProject: (projectId: number) => Promise<void>;
  clearProject: () => void;
  refreshProjects: () => Promise<void>;
  isLoading: boolean;
  error: string | null;
  hasSingleProject: boolean;
}

const ProjectContext = createContext<ProjectContextType | undefined>(undefined);

const STORAGE_KEY = 'vulnscanner_current_project_id';

export const ProjectProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [currentProject, setCurrentProjectState] = useState<Project | null>(null);
  const [projects, setProjects] = useState<Project[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [projectsLoaded, setProjectsLoaded] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();

  // Extract projectId from URL if present
  const getProjectIdFromUrl = useCallback((): number | null => {
    const match = location.pathname.match(/\/projects\/(\d+)/);
    return match ? parseInt(match[1], 10) : null;
  }, [location.pathname]);

  // Load all projects
  const refreshProjects = useCallback(async () => {
    try {
      const response = await projectsAPI.getAll();
      setProjects(response.data || []);
      setProjectsLoaded(true);
      return response.data || [];
    } catch (err: any) {
      console.error('Failed to load projects:', err);
      setProjects([]);
      setProjectsLoaded(true);
      return [];
    }
  }, []);

  // Load project by ID
  const loadProject = useCallback(async (projectId: number) => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await projectsAPI.getById(projectId);
      setCurrentProjectState(response.data);
      localStorage.setItem(STORAGE_KEY, projectId.toString());
    } catch (err: any) {
      console.error('Failed to load project:', err);
      setError(err.response?.data?.error || 'Failed to load project');
      setCurrentProjectState(null);
      localStorage.removeItem(STORAGE_KEY);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Select a project (used when user clicks on a project)
  const selectProject = useCallback(async (projectId: number) => {
    await loadProject(projectId);
  }, [loadProject]);

  // Set project directly (for when we already have the project data)
  const setCurrentProject = useCallback((project: Project | null) => {
    setCurrentProjectState(project);
    if (project) {
      localStorage.setItem(STORAGE_KEY, project.id.toString());
    } else {
      localStorage.removeItem(STORAGE_KEY);
    }
  }, []);

  // Clear project context
  const clearProject = useCallback(() => {
    setCurrentProjectState(null);
    localStorage.removeItem(STORAGE_KEY);
  }, []);

  // Load projects on mount (only when authenticated)
  useEffect(() => {
    if (user) {
      refreshProjects();
    }
  }, [user, refreshProjects]);

  // Auto-select if user has only one project
  useEffect(() => {
    if (projectsLoaded && projects.length === 1 && !currentProject) {
      const singleProject = projects[0];
      setCurrentProject(singleProject);

      // If on projects list page, auto-navigate to the single project
      if (location.pathname === '/projects') {
        navigate(`/projects/${singleProject.id}`, { replace: true });
      }
    }
  }, [projectsLoaded, projects, currentProject, setCurrentProject, location.pathname, navigate]);

  // Sync with URL changes
  useEffect(() => {
    const urlProjectId = getProjectIdFromUrl();

    if (urlProjectId) {
      // We're on a project page - load project if not already loaded or different
      if (!currentProject || currentProject.id !== urlProjectId) {
        loadProject(urlProjectId);
      }
    }
    // Don't clear project when navigating away - keep context for sidebar
  }, [location.pathname, getProjectIdFromUrl, currentProject, loadProject]);

  const hasSingleProject = projectsLoaded && projects.length === 1;

  const value = useMemo(() => ({
    currentProject,
    projects,
    setCurrentProject,
    selectProject,
    clearProject,
    refreshProjects,
    isLoading,
    error,
    hasSingleProject,
  }), [currentProject, projects, setCurrentProject, selectProject, clearProject, refreshProjects, isLoading, error, hasSingleProject]);

  return (
    <ProjectContext.Provider value={value}>
      {children}
    </ProjectContext.Provider>
  );
};

export const useProject = (): ProjectContextType => {
  const context = useContext(ProjectContext);
  if (context === undefined) {
    throw new Error('useProject must be used within a ProjectProvider');
  }
  return context;
};

export default ProjectContext;
